<template>
  <div class="font-selector">
    <!-- Font Type Selection -->
    <div class="mb-3">
      <div class="btn-group btn-group-toggle">
        <label class="btn btn-outline-secondary"
               :class="{'active': fontType === 'default'}"
               @click="setFontType('default')">
          <input type="radio" name="font-type" value="default" v-model="fontType">
          <translate translate-context="form_configurator.font">Keep default</translate>
        </label>
        <label class="btn btn-outline-secondary"
               :class="{'active': fontType === 'google'}"
               @click="setFontType('google')">
          <input type="radio" name="font-type" value="google" v-model="fontType">
          Google Fonts
        </label>
        <label class="btn btn-outline-secondary"
               :class="{'active': fontType === 'custom'}"
               @click="setFontType('custom')">
          <input type="radio" name="font-type" value="custom" v-model="fontType">
          <translate translate-context="form_configurator.font">Custom</translate>
        </label>
      </div>
    </div>

    <!-- Google Fonts Input -->
    <div v-if="fontType === 'google'" class="mb-3">
      <div class="input-group">
        <input
            type="text"
            class="form-control"
            v-model="googleFontInput"
            :placeholder="$pgettext('form_configurator.font', 'Paste Google Fonts URL or font name')"
            @input="onGoogleFontInput"
            :class="{'is-invalid': googleFontError, 'is-valid': googleFontValid}"
        >
        <div class="input-group-append" v-if="isValidating">
          <span class="input-group-text">
            <CSpinner size="sm" color="primary"/>
          </span>
        </div>
      </div>
      <div v-if="googleFontError" class="invalid-feedback d-block">
        {{ googleFontError }}
      </div>
      <div v-if="googleFontValid" class="valid-feedback d-block">
        <translate translate-context="form_configurator.font">Font found:</translate> <strong>{{ resolvedFontName }}</strong>
      </div>
      <div class="mt-2 text-muted small">
        <translate translate-context="form_configurator.font_help">
          Go to Google Fonts, find a font you like, and paste the URL here
        </translate>
        <br>
        <span class="text-monospace">https://fonts.google.com/specimen/Font+Name</span>
      </div>
      <div class="mt-2">
        <a href="https://fonts.google.com/" target="_blank" rel="noopener noreferrer" class="text-primary">
          <CIcon name="cilExternalLink" size="sm" class="mr-1"/>
          <translate translate-context="form_configurator.font_help">Open Google Fonts</translate>
        </a>
      </div>
    </div>

    <!-- Custom Font Upload -->
    <div v-if="fontType === 'custom'" class="mb-3">
      <PUploader
          :params="uploadParams"
          :get_intent="getFontUploadIntent"
          :value.sync="fontIntentId"
          :preview="customFontPreviewUrl"
          :filename="customFontName"
          :help_text="$pgettext('form_configurator.font_help', 'Upload a .woff or .woff2 font file (max 2MB)')"
          @uploaded="onFontUploaded"
      />
      <div class="mt-2 text-muted small">
        <translate translate-context="form_configurator.font_help">
          WOFF/WOFF2 are web font formats. If you have a .ttf or .otf file, convert it first.
        </translate>
        <br>
        <a href="https://cloudconvert.com/font-converter" target="_blank" rel="noopener noreferrer" class="text-primary">
          <CIcon name="cilExternalLink" size="sm" class="mr-1"/>
          <translate translate-context="form_configurator.font_help">Convert to WOFF/WOFF2</translate>
        </a>
      </div>
    </div>

    <!-- Font Preview (isolated iframe) -->
    <div v-if="fontType !== 'default' && showPreview" class="font-preview-container">
      <iframe
          ref="previewIframe"
          class="font-preview-iframe"
          :srcdoc="previewIframeContent"
          sandbox="allow-same-origin"
      ></iframe>
    </div>

    <!-- Font Scale Slider -->
    <div class="mt-3">
      <label class="form-label d-flex justify-content-between">
        <span><translate translate-context="form_configurator.font">Text size</translate></span>
        <span class="text-muted">{{ Math.round(fontScale * 100) }}%</span>
      </label>
      <input
          type="range"
          class="form-range"
          min="0.8"
          max="1.5"
          step="0.05"
          :value="fontScale"
          @input="onScaleChange($event.target.value)"
      >
      <div class="d-flex justify-content-between text-muted small">
        <span>80%</span>
        <span>100%</span>
        <span>150%</span>
      </div>
    </div>
  </div>
</template>

<script>
import PUploader from "@/domain/core/components/PUploader.vue";
import {debounce} from "lodash";

export default {
  name: 'FontSelector',
  components: {PUploader},
  props: {
    value: {
      type: Object,
      default: () => ({
        type: 'default',
        google_font: null,
        custom_font_url: null,
        custom_font_name: null,
        scale: 1
      })
    },
    getFontUploadIntent: {
      type: Function,
      required: true
    },
    customFontPreviewUrl: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      googleFontInput: '',
      googleFontError: null,
      googleFontValid: false,
      isValidating: false,
      resolvedFontName: ''
    }
  },
  computed: {
    fontType: {
      get() {
        const type = this.value?.type
        // Handle legacy 'system' value
        if (type === 'system') return 'default'
        return type || 'default'
      },
      set(val) {
        this.updateValue({type: val})
      }
    },
    fontIntentId: {
      get() {
        return this.value?.custom_font_url
      },
      set(val) {
        // Pass through the value (including 'delete' key) - parent handles the logic
        const newValue = {...this.value, custom_font_url: val}
        this.$emit('input', newValue)
      }
    },
    customFontName() {
      return this.value?.custom_font_name
    },
    fontScale() {
      return this.value?.scale || 1
    },
    uploadParams() {
      return {
        allowed_extensions: ['woff', 'woff2'],
        max_generic_size: 2000000 // 2MB max
      }
    },
    showPreview() {
      if (this.fontType === 'custom') {
        return !!this.customFontPreviewUrl
      }
      return this.fontType === 'google' && this.googleFontValid && this.googleFontInput
    },
    previewIframeContent() {
      let fontLoadingHtml = ''
      let fontFamily = 'sans-serif'

      if (this.fontType === 'google' && this.googleFontValid && this.resolvedFontName) {
        const encodedFont = this.resolvedFontName.replace(/ /g, '+')
        fontLoadingHtml = `<link href="https://fonts.googleapis.com/css2?family=${encodedFont}:wght@400;700&display=swap" rel="stylesheet">`
        fontFamily = `'${this.resolvedFontName}', sans-serif`
      } else if (this.fontType === 'custom' && this.customFontPreviewUrl && this.customFontName) {
        fontLoadingHtml = `
          <style>
            @font-face {
              font-family: '${this.customFontName}';
              src: url('${this.customFontPreviewUrl}') format('woff2');
              font-weight: normal;
              font-style: normal;
            }
          </style>
        `
        fontFamily = `'${this.customFontName}', sans-serif`
      }

      const scale = this.fontScale

      return `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          ${fontLoadingHtml}
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; }
            html {
              font-size: calc(16px * ${scale});
            }
            body {
              font-family: ${fontFamily};
              padding: 1rem;
              background: #f8f9fa;
              display: flex;
              flex-direction: column;
              gap: 0.5rem;
            }
            .preview-title {
              font-size: 1.5rem;
              font-weight: 700;
            }
            .preview-text {
              font-size: 1rem;
              font-weight: 400;
              color: #333;
            }
            .preview-small {
              font-size: 0.75rem;
              color: #666;
            }
          </style>
        </head>
        <body>
          <div class="preview-title">The quick brown fox</div>
          <div class="preview-text">jumps over the lazy dog</div>
          <div class="preview-small">ABCDEFGHIJKLMNOPQRSTUVWXYZ abcdefghijklmnopqrstuvwxyz 0123456789</div>
        </body>
        </html>
      `
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal) {
        if (newVal?.google_font && newVal.type === 'google') {
          this.googleFontInput = newVal.google_font
          this.resolvedFontName = newVal.google_font
          this.googleFontValid = true
        }
      }
    }
  },
  created() {
    this.debouncedValidateGoogleFont = debounce(this.validateGoogleFont, 500)
  },
  methods: {
    updateValue(changes) {
      const newValue = {...this.value, ...changes}
      this.$emit('input', newValue)
      this.$emit('change')
    },
    setFontType(type) {
      this.googleFontError = null
      this.googleFontValid = false
      this.updateValue({type})
    },
    onGoogleFontInput() {
      this.googleFontError = null
      this.googleFontValid = false
      this.resolvedFontName = ''
      if (this.googleFontInput.trim()) {
        this.debouncedValidateGoogleFont(this.googleFontInput.trim())
      }
    },
    extractFontNameFromInput(input) {
      // Check if it's a Google Fonts URL
      // Formats:
      //   https://fonts.google.com/specimen/Atkinson+Hyperlegible+Mono
      //   https://fonts.google.com/specimen/Roboto?query=roboto
      const urlMatch = input.match(/fonts\.google\.com\/specimen\/([^?]+)/)
      if (urlMatch) {
        // Replace + with spaces and decode URI
        return decodeURIComponent(urlMatch[1].replace(/\+/g, ' '))
      }
      // Otherwise treat as font name directly
      return input.trim()
    },
    async validateGoogleFont(input) {
      this.isValidating = true
      this.googleFontError = null
      this.googleFontValid = false

      try {
        const fontName = this.extractFontNameFromInput(input)
        this.resolvedFontName = fontName

        // Use + for spaces (Google Fonts API convention) without double-encoding
        const encodedFont = fontName.replace(/ /g, '+')
        const response = await fetch(
            `https://fonts.googleapis.com/css2?family=${encodedFont}:wght@400;700&display=swap`
        )

        if (response.ok) {
          this.googleFontValid = true
          this.updateValue({google_font: fontName})
        } else {
          this.googleFontError = this.$pgettext('form_configurator.font', 'Font not found on Google Fonts')
          this.resolvedFontName = ''
        }
      } catch (error) {
        this.googleFontError = this.$pgettext('form_configurator.font', 'Error validating font')
        this.resolvedFontName = ''
      } finally {
        this.isValidating = false
      }
    },
    onFontUploaded() {
      // Just emit to parent - it handles DELETE_KEY check like logo does
      this.$emit('fontUploaded')
    },
    onScaleChange(value) {
      this.updateValue({scale: parseFloat(value)})
    }
  }
}
</script>

<style scoped>
.font-selector .btn-group-toggle .btn {
  cursor: pointer;
}

.font-selector .btn-group-toggle input[type="radio"] {
  display: none;
}

.font-preview-container {
  margin-top: 1rem;
  border: 1px solid #d8dbe0;
  border-radius: 4px;
  overflow: hidden;
}

.font-preview-iframe {
  width: 100%;
  height: 120px;
  border: none;
  display: block;
}
</style>
